import React, { useState } from 'react';
import {
  ExclamationTriangleIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '../ui';
import { cn } from '../../utils/helpers';

/* -------------------------------------------------------------------------- */
/*  Building blocks                                                           */
/* -------------------------------------------------------------------------- */

export interface CodeBlockProps {
  code: string;
  language?: string;
  title?: string;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language = 'bash', title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-lg overflow-hidden border border-secondary-200 dark:border-secondary-700 bg-secondary-900">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-secondary-800 border-b border-secondary-700">
          <span className="text-sm text-secondary-300">{title}</span>
          <Badge variant="secondary" className="text-xs">{language}</Badge>
        </div>
      )}
      <div className="relative">
        <pre className="p-4 overflow-x-auto text-sm text-secondary-100">
          <code>{code}</code>
        </pre>
        <button
          onClick={handleCopy}
          className="absolute top-2 right-2 p-2 rounded-lg bg-secondary-700 hover:bg-secondary-600 transition-colors"
          title="Copy to clipboard"
        >
          {copied ? (
            <CheckIcon className="w-4 h-4 text-success-400" />
          ) : (
            <ClipboardDocumentIcon className="w-4 h-4 text-secondary-300" />
          )}
        </button>
      </div>
    </div>
  );
};

export interface CollapsibleSectionProps {
  title: string;
  badge?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, badge, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-secondary-50 dark:bg-secondary-800 hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
      >
        <span className="flex items-center gap-2 font-medium text-secondary-900 dark:text-secondary-100">
          {title}
          {badge && <Badge variant="secondary" className="text-xs font-normal">{badge}</Badge>}
        </span>
        {isOpen ? (
          <ChevronDownIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
        ) : (
          <ChevronRightIcon className="w-5 h-5 text-secondary-500 dark:text-secondary-400" />
        )}
      </button>
      {isOpen && <div className="p-4 border-t border-secondary-200 dark:border-secondary-700 space-y-4">{children}</div>}
    </div>
  );
};

export const EndpointCard: React.FC<{
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  endpoint: string;
  description: string;
  children?: React.ReactNode;
}> = ({ method, endpoint, description, children }) => {
  const methodColors = {
    GET: 'bg-success-100 text-success-700',
    POST: 'bg-primary-100 text-primary-700',
    PUT: 'bg-warning-100 text-warning-700',
    PATCH: 'bg-accent-100 text-accent-700',
    DELETE: 'bg-danger-100 text-danger-700',
  };

  return (
    <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className={cn('px-2 py-1 rounded text-xs font-bold', methodColors[method])}>
          {method}
        </span>
        <div className="flex-1">
          <code className="text-sm font-mono text-secondary-900 dark:text-secondary-100">{endpoint}</code>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
};

export interface Field {
  name: string;
  type: string;
  required?: boolean;
  description: string;
}

export const FieldTable: React.FC<{ fields: Field[] }> = ({ fields }) => (
  <div className="overflow-x-auto border border-secondary-200 dark:border-secondary-700 rounded-lg">
    <table className="w-full text-sm">
      <thead className="bg-secondary-50 dark:bg-secondary-800">
        <tr>
          <th className="text-left font-medium text-secondary-600 dark:text-secondary-300 px-4 py-2">Field</th>
          <th className="text-left font-medium text-secondary-600 dark:text-secondary-300 px-4 py-2">Type</th>
          <th className="text-left font-medium text-secondary-600 dark:text-secondary-300 px-4 py-2 whitespace-nowrap">Required</th>
          <th className="text-left font-medium text-secondary-600 dark:text-secondary-300 px-4 py-2">Description</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
        {fields.map((field) => (
          <tr key={field.name} className="align-top">
            <td className="px-4 py-2">
              <code className="font-mono text-primary-600 dark:text-primary-400 whitespace-nowrap">{field.name}</code>
            </td>
            <td className="px-4 py-2 text-secondary-500 dark:text-secondary-400 font-mono text-xs whitespace-nowrap">{field.type}</td>
            <td className="px-4 py-2">
              <Badge variant={field.required ? 'primary' : 'secondary'} className="text-xs">
                {field.required ? 'Required' : 'Optional'}
              </Badge>
            </td>
            <td className="px-4 py-2 text-secondary-600 dark:text-secondary-400">{field.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const SimpleTable: React.FC<{ headers: string[]; rows: React.ReactNode[][] }> = ({ headers, rows }) => (
  <div className="overflow-x-auto border border-secondary-200 dark:border-secondary-700 rounded-lg">
    <table className="w-full text-sm">
      <thead className="bg-secondary-50 dark:bg-secondary-800">
        <tr>
          {headers.map((header) => (
            <th key={header} className="text-left font-medium text-secondary-600 dark:text-secondary-300 px-4 py-2">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
        {rows.map((row, index) => (
          <tr key={index} className="align-top">
            {row.map((cell, cellIndex) => (
              <td key={cellIndex} className="px-4 py-2 text-secondary-600 dark:text-secondary-400">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Mono: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="font-mono text-xs text-primary-600 dark:text-primary-400">{children}</code>
);

export const Callout: React.FC<{ variant?: 'info' | 'warning'; title: string; children: React.ReactNode }> = ({
  variant = 'info',
  title,
  children,
}) => (
  <div
    className={cn(
      'flex items-start gap-3 p-4 rounded-lg border',
      variant === 'warning'
        ? 'bg-warning-50 border-warning-200 dark:bg-warning-900/20 dark:border-warning-800'
        : 'bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800'
    )}
  >
    {variant === 'warning' ? (
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5 text-warning-600 dark:text-warning-400" />
    ) : (
      <SparklesIcon className="w-5 h-5 shrink-0 mt-0.5 text-primary-600 dark:text-primary-400" />
    )}
    <div>
      <h4 className="font-medium text-secondary-900 dark:text-secondary-100">{title}</h4>
      <div className="text-sm text-secondary-600 dark:text-secondary-400 mt-1 space-y-2">{children}</div>
    </div>
  </div>
);

export const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-lg font-semibold text-secondary-900 dark:text-secondary-100">{children}</h3>
);
